/**
 * Tabs for the size guide popup (snippets/pacc-size-guide-drawer.liquid):
 * "Size Chart" / "Measuring Guide". Click or use the arrow keys to switch.
 */
class PaccTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    for (const tab of this.tabs) {
      tab.addEventListener('click', this.#onClick);
      tab.addEventListener('keydown', this.#onKeydown);
    }
  }

  disconnectedCallback() {
    for (const tab of this.tabs ?? []) {
      tab.removeEventListener('click', this.#onClick);
      tab.removeEventListener('keydown', this.#onKeydown);
    }
  }

  /**
   * Shows a tab by name ("chart", "guide", "fit"); ignored if it isn't there.
   * @param {string} name
   */
  selectTab(name) {
    const tab = this.tabs?.find((tab) => tab.id === `SizeGuideTab-${name}`);
    if (tab) this.#select(tab);
  }

  /** @param {Element} selected */
  #select(selected) {
    for (const tab of this.tabs) {
      const isSelected = tab === selected;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;

      const panel = document.getElementById(tab.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !isSelected;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => this.#select(/** @type {Element} */ (event.currentTarget));

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();

    const index = this.tabs.indexOf(/** @type {Element} */ (event.currentTarget));
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const next = this.tabs[(index + step + this.tabs.length) % this.tabs.length];
    this.#select(next);
    /** @type {HTMLElement} */ (next).focus();
  };
}

if (!customElements.get('pacc-tabs')) customElements.define('pacc-tabs', PaccTabs);

// Links that open the size guide on a given tab, e.g. "Firearm Fit Guide"
// (snippets/pacc-size-guide-link.liquid: data-size-guide-tab="fit")
document.addEventListener('click', (event) => {
  const link = /** @type {Element} */ (event.target).closest?.('[data-size-guide-tab]');
  if (!(link instanceof HTMLElement)) return;
  const tabs = /** @type {PaccTabs | null} */ (document.querySelector('#size-guide-drawer pacc-tabs'));
  tabs?.selectTab(link.dataset.sizeGuideTab ?? 'chart');
});

/**
 * Measuring guide image: clicking it opens a full-size view in a nested modal
 * <dialog>. Escape and backdrop clicks close only the zoom, not the size guide
 * (theme-drawer listens for both), so those events stop here.
 */
class PaccImageZoom extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.pacc-size-guide__zoom-button');
    this.dialog = this.querySelector('dialog');
    this.button?.addEventListener('click', this.#open);
    this.dialog?.addEventListener('click', this.#onDialogClick);
    this.dialog?.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#open);
    this.dialog?.removeEventListener('click', this.#onDialogClick);
    this.dialog?.removeEventListener('keydown', this.#onKeydown);
  }

  #open = () => {
    const image = this.dialog?.querySelector('img');
    if (image) image.loading = 'eager';
    this.dialog?.showModal();
  };

  #close() {
    this.dialog?.close();
    /** @type {HTMLElement | null | undefined} */ (this.button)?.focus({ preventScroll: true });
  }

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    event.stopPropagation();
    const target = /** @type {Element} */ (event.target);
    // Any click closes: the backdrop, the close button, or the image itself
    if (target === this.dialog || target.closest('.pacc-image-zoom__close, img')) this.#close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    this.#close();
  };
}

if (!customElements.get('pacc-image-zoom')) customElements.define('pacc-image-zoom', PaccImageZoom);
